/**
 * photoCheck — ตรวจรูปยืนยันตัวตนเบื้องต้น "ในเครื่องผู้ใช้" (ไม่ส่งรูปออกไปที่ไหน) ก่อนให้แนบส่ง
 *
 *   kind = 'holdcard' : รูปถ่ายตนเองถือบัตร ปชช. — ต้องเห็น "ใบหน้าคนจริง (ใหญ่)" + "บัตร (รูปหน้าเล็กบนบัตร)"
 *   kind = 'idcard'   : รูปด้านหน้าบัตร ปชช. อย่างเดียว — ต้องเห็นรูปหน้าบนบัตร และต้องไม่ใช่รูปเซลฟี่
 *
 * และตรวจว่า "ภาพกลับด้าน (mirror)" หรือไม่ จากตำแหน่งชิปสีทองบนบัตร:
 *   บัตร ปชช.ไทย ชิปอยู่ซ้าย รูปหน้าอยู่ขวาเสมอ → ถ้าพบชิปอยู่ "ขวา" ของรูปหน้าบนบัตร = ภาพกลับด้าน
 *
 * ใช้ MediaPipe BlazeFace (โมเดล ~230KB + wasm โหลดเฉพาะตอนใช้ จาก /mediapipe ของเราเอง)
 * BlazeFace ย่อภาพเหลือ 128px ก่อนตรวจ หน้าเล็กๆ บนบัตรจึงหลุด — แก้โดยซอยภาพเป็นช่อง (tile) แล้วตรวจซ้ำทีละช่อง
 */

const ASSET_BASE = ((typeof process !== 'undefined' && process.env && process.env.PUBLIC_URL) || '') + '/mediapipe';
const MAX_SIDE = 1600;          // ย่อภาพก่อนวิเคราะห์ (เร็วขึ้น ผลไม่ต่าง)
const TILE_INPUT = 256;         // ขนาดช่องที่ป้อนให้ตัวตรวจ

const TILE_MIN_SCORE = 0.75;   // คะแนนขั้นต่ำของหน้าที่เจอจากช่องย่อย

const CHIP_MIN_SCORE = 0.4;     // คะแนนขั้นต่ำที่ถือว่า "เจอชิป"
const CHIP_MIN_MARGIN = 0.2;    // ซ้าย-ขวาต้องต่างกันอย่างน้อยเท่านี้จึงตัดสิน

let detectorPromise = null;

function loadDetector() {
    if (!detectorPromise) {
        detectorPromise = (async () => {
            const { FaceDetector, FilesetResolver } = await import('@mediapipe/tasks-vision');
            const fileset = await FilesetResolver.forVisionTasks(`${ASSET_BASE}/wasm`);
            return FaceDetector.createFromOptions(fileset, {
                baseOptions: { modelAssetPath: `${ASSET_BASE}/blaze_face_short_range.tflite`, delegate: 'CPU' },
                runningMode: 'IMAGE',
                minDetectionConfidence: 0.5
            });
        })();
        // โหลดพลาด (เน็ตหลุด ฯลฯ) → ให้ลองใหม่ได้ในครั้งถัดไป
        detectorPromise.catch(() => { detectorPromise = null; });
    }
    return detectorPromise;
}

/** เรียกล่วงหน้าตอนเปิดกล้อง เพื่อให้โมเดลพร้อมก่อนผู้ใช้กดถ่าย */
export function preloadPhotoCheck() {
    loadDetector().catch(() => {});
}

/** File/Blob → canvas (เคารพ EXIF orientation) — คืน null ถ้าเบราว์เซอร์ถอดรหัสไม่ได้ (เช่น HEIC บน Android) */
export async function fileToCanvas(file, maxSide = 0) {
    let src = null;
    try {
        if (typeof createImageBitmap === 'function') {
            try { src = await createImageBitmap(file, { imageOrientation: 'from-image' }); } catch (e) { src = null; }
        }
        if (!src) {
            src = await new Promise((resolve, reject) => {
                const url = URL.createObjectURL(file);
                const img = new Image();
                img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
                img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('decode-failed')); };
                img.src = url;
            });
        }
    } catch (e) {
        return null;
    }
    const w = src.width || src.naturalWidth;
    const h = src.height || src.naturalHeight;
    if (!w || !h) return null;
    const scale = maxSide ? Math.min(1, maxSide / Math.max(w, h)) : 1;
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(w * scale);
    canvas.height = Math.round(h * scale);
    canvas.getContext('2d').drawImage(src, 0, 0, canvas.width, canvas.height);
    if (src.close) src.close();
    return canvas;
}

/** กลับด้านซ้าย-ขวา */
export function flipCanvas(canvas) {
    const out = document.createElement('canvas');
    out.width = canvas.width;
    out.height = canvas.height;
    const ctx = out.getContext('2d');
    ctx.translate(out.width, 0);
    ctx.scale(-1, 1);
    ctx.drawImage(canvas, 0, 0);
    return out;
}

export function canvasToFile(canvas, name) {
    return new Promise((resolve, reject) => {
        canvas.toBlob((blob) => {
            if (!blob) return reject(new Error('encode-failed'));
            resolve(new File([blob], name || `photo-${Date.now()}.jpg`, { type: 'image/jpeg' }));
        }, 'image/jpeg', 0.92);
    });
}

function iou(a, b) {
    const x1 = Math.max(a.x, b.x), y1 = Math.max(a.y, b.y);
    const x2 = Math.min(a.x + a.w, b.x + b.w), y2 = Math.min(a.y + a.h, b.y + b.h);
    const inter = Math.max(0, x2 - x1) * Math.max(0, y2 - y1);
    return inter / (a.w * a.h + b.w * b.h - inter || 1);
}

/**
 * ทิศของใบหน้าจากจุดตา-ปาก → ต้องหมุนภาพตามเข็มกี่องศา (0/90/180/270) หน้าจึงจะตั้งตรง
 * keypoints ของ BlazeFace: [ตาขวา, ตาซ้าย, จมูก, ปาก, ...] (พิกัด 0..1 ของภาพที่ป้อน)
 */
function faceTurn(kp, w, h) {
    if (!kp || kp.length < 4) return 0;
    const vx = (kp[3].x - (kp[0].x + kp[1].x) / 2) * w;
    const vy = (kp[3].y - (kp[0].y + kp[1].y) / 2) * h;
    if (Math.abs(vy) >= Math.abs(vx)) return vy >= 0 ? 0 : 180;
    return vx > 0 ? 90 : 270;
}

/** หมุนภาพตามเข็ม 90/180/270 องศา */
export function rotateCanvas(canvas, deg) {
    if (!deg) return canvas;
    const out = document.createElement('canvas');
    const swap = deg === 90 || deg === 270;
    out.width = swap ? canvas.height : canvas.width;
    out.height = swap ? canvas.width : canvas.height;
    const ctx = out.getContext('2d');
    ctx.translate(out.width / 2, out.height / 2);
    ctx.rotate((deg * Math.PI) / 180);
    ctx.drawImage(canvas, -canvas.width / 2, -canvas.height / 2);
    return out;
}

function detectRegion(detector, canvas, scratch, rx, ry, rw, rh) {
    // ย่อ/ขยายช่องให้ด้านยาว = TILE_INPUT เสมอ (ช่องเล็กก็ขยายขึ้น — หน้าบนบัตรเล็กมาก ขยายแล้วตรวจเจอง่ายกว่า)
    const s = TILE_INPUT / Math.max(rw, rh);
    scratch.width = Math.max(1, Math.round(rw * s));
    scratch.height = Math.max(1, Math.round(rh * s));
    scratch.getContext('2d').drawImage(canvas, rx, ry, rw, rh, 0, 0, scratch.width, scratch.height);
    const res = detector.detect(scratch);
    return (res.detections || []).filter((d) => d.boundingBox).map((d) => ({
        x: rx + d.boundingBox.originX / s,
        y: ry + d.boundingBox.originY / s,
        w: d.boundingBox.width / s,
        h: d.boundingBox.height / s,
        score: (d.categories && d.categories[0] && d.categories[0].score) || 0,
        turn: faceTurn(d.keypoints, scratch.width, scratch.height)
    }));
}

/**
 * ตรวจหน้าทั้งภาพ แล้วซอยช่องละเอียดขึ้นทีละระดับ เพื่อเก็บหน้าเล็กบนบัตร — รวมผลซ้ำด้วย NMS
 * หยุดทันทีที่ isEnough(faces) เป็นจริง (ส่วนใหญ่จบที่ระดับ 1–2 ระดับละเอียดสุดใช้เฉพาะรูปที่บัตรเล็ก/ไกล)
 */
function detectAllFaces(detector, canvas, levels, isEnough) {
    const W = canvas.width, H = canvas.height;
    const scratch = document.createElement('canvas');
    const merge = (list) => {
        list.sort((p, q) => q.score - p.score);
        const kept = [];
        list.forEach((f) => { if (!kept.some((k) => iou(k, f) > 0.3)) kept.push(f); });
        return kept;
    };
    let faces = merge(detectRegion(detector, canvas, scratch, 0, 0, W, H));
    const shortSide = Math.min(W, H);
    const tiles = [shortSide / 2, shortSide / 4, shortSide / 6].slice(0, levels);
    for (let t = 0; t < tiles.length && !isEnough(faces); t++) {
        const tile = tiles[t], step = tile / 2;
        for (let y = 0; y < H - step / 2; y += step) {
            for (let x = 0; x < W - step / 2; x += step) {
                const rw = Math.min(tile, W - x), rh = Math.min(tile, H - y);
                // ช่องย่อยมักเจอของปลอม (ตัวหนังสือ/ลายเสื้อ คะแนนต่ำ) — รับเฉพาะที่มั่นใจสูง
                faces = faces.concat(detectRegion(detector, canvas, scratch, x, y, rw, rh).filter((f) => f.score >= TILE_MIN_SCORE));
            }
        }
        faces = merge(faces);
    }
    return faces;
}

/** พิกเซลโทน "ทองของชิป" (เหลือง-ส้ม อิ่มสีพอควร สว่าง) — สีผิวก็เข้าเกณฑ์นี้ได้ จึงต้องดูรูปทรงประกอบ (ดู mirrorFromChip) */
function isGold(r, g, b) {
    const mx = Math.max(r, g, b), mn = Math.min(r, g, b);
    if (mx < 100 || mx - mn < mx * 0.2) return false;  // มืดไป / สีจางไป
    if (!(r >= g && g > b)) return false;               // โทนเหลือง-ส้ม
    const hue = 60 * ((g - b) / (mx - mn));             // r เป็นค่าสูงสุด → hue 0..60
    return hue >= 25;
}

/**
 * ดูว่าชิปทองอยู่ซ้ายหรือขวาของ "รูปหน้าบนบัตร"
 * ชิป = ก้อนสีทองขนาด ~1.2 เท่าของหน้าบนบัตร ที่ "ล้อมด้วยพื้นบัตรสีฟ้าขาว" → คะแนน = ทองข้างใน − ทองรอบนอก
 * (มือ/แขนที่ถือบัตรเป็นสีโทนเดียวกันแต่เป็นผืนใหญ่ รอบนอกก็เป็นทองด้วย คะแนนจึงต่ำ)
 * คืน mirrored: false = ปกติ (ชิปซ้าย), true = ภาพกลับด้าน (ชิปขวา), null = ไม่ชัดพอจะตัดสิน
 */
function mirrorFromChip(canvas, cardFace) {
    const U = 8;                                  // ย่อให้ความกว้างหน้าบนบัตร = 8px
    const fw = cardFace.w;
    const cx = cardFace.x + cardFace.w / 2, cy = cardFace.y + cardFace.h / 2;
    const spanX = 10, up = 4.5, down = 2.5;       // กรอบค้นหา (หน่วย = ความกว้างหน้าบนบัตร)
    const mw = spanX * 2 * U, mh = (up + down) * U;
    const small = document.createElement('canvas');
    small.width = mw; small.height = mh;
    const sctx = small.getContext('2d');
    sctx.fillStyle = '#000';
    sctx.fillRect(0, 0, mw, mh);
    sctx.drawImage(canvas, cx - spanX * fw, cy - up * fw, spanX * 2 * fw, (up + down) * fw, 0, 0, mw, mh);
    const d = sctx.getImageData(0, 0, mw, mh).data;
    // integral image ของ mask สีทอง
    const iw = mw + 1;
    const I = new Float64Array(iw * (mh + 1));
    for (let y = 0; y < mh; y++) {
        let row = 0;
        for (let x = 0; x < mw; x++) {
            const i = (y * mw + x) * 4;
            if (isGold(d[i], d[i + 1], d[i + 2])) row++;
            I[(y + 1) * iw + x + 1] = I[y * iw + x + 1] + row;
        }
    }
    const sum = (x0, y0, x1, y1) => {
        x0 = Math.max(0, x0); y0 = Math.max(0, y0); x1 = Math.min(mw, x1); y1 = Math.min(mh, y1);
        if (x1 <= x0 || y1 <= y0) return 0;
        return I[y1 * iw + x1] - I[y0 * iw + x1] - I[y1 * iw + x0] + I[y0 * iw + x0];
    };
    const S = Math.round(1.2 * U), R = Math.round(S * 0.6);   // ขนาดชิป / ความหนาวงรอบนอก
    const best = (xa, xb) => {
        let top = 0;
        for (let y = R; y + S + R <= mh; y++) {
            for (let x = xa; x + S <= xb; x++) {
                const inner = sum(x, y, x + S, y + S);
                const outer = sum(x - R, y - R, x + S + R, y + S + R) - inner;
                const outerArea = (S + 2 * R) * (S + 2 * R) - S * S;
                const score = inner / (S * S) - outer / outerArea;
                if (score > top) top = score;
            }
        }
        return top;
    };
    const mid = spanX * U;
    // ชิปอยู่ห่างกึ่งกลางรูปหน้าบนบัตรราว 4–9 เท่าของความกว้างหน้า
    const left = best(mid - Math.round(9.5 * U), mid - Math.round(3.5 * U));
    const right = best(mid + Math.round(3.5 * U), mid + Math.round(9.5 * U));
    const result = { left: +left.toFixed(2), right: +right.toFixed(2), mirrored: null };
    if (Math.max(left, right) >= CHIP_MIN_SCORE && Math.abs(left - right) >= CHIP_MIN_MARGIN) {
        result.mirrored = right > left;
    }
    return result;
}


export const PHOTO_CHECK_MESSAGES = {
    'no-face': 'ไม่พบใบหน้าของท่านในรูป — กรุณาถ่ายให้เห็น "ใบหน้า" พร้อม "ถือบัตรประชาชน" ในรูปเดียวกัน (ส่งแต่รูปบัตรไม่ได้)',
    'no-card': 'ไม่พบบัตรประชาชนในรูป — กรุณาถือบัตร (ด้านหน้า) ให้อยู่ในกรอบ เห็นรูปบนบัตรชัดเจน ไม่เบลอ ไม่มีแสงสะท้อน และอย่าให้นิ้วบังรูปบนบัตร',
    'idcard-is-selfie': 'รูปนี้ดูเป็นรูปถ่ายบุคคล ไม่ใช่รูปบัตร — ช่องนี้ให้ถ่าย "เฉพาะด้านหน้าบัตรประชาชน" ใกล้ๆ ให้เต็มกรอบ',
    'idcard-not-found': 'ไม่พบด้านหน้าบัตรประชาชนในรูป — กรุณาถ่าย "ด้านหน้าบัตร" (ด้านที่มีรูปหน้า) ให้ชัด ไม่เบลอ ไม่มีแสงสะท้อน'
};

function analyzeUpright(detector, canvas, kind) {
    // เกณฑ์ขนาดเทียบกับ "ขนาดภาพเฉลี่ย" (ใช้ได้ทั้งแนวตั้ง/แนวนอน)
    const size = Math.sqrt(canvas.width * canvas.height);
    const largest = (list) => list.reduce((m, f) => (!m || f.w * f.h > m.w * m.h ? f : m), null);
    // หน้าคนจริงต้องใหญ่พอ (รูปหน้าบนบัตรที่ถ่ายใกล้ๆ จะกว้างไม่ถึงเกณฑ์นี้)
    const findMain = (list) => largest(list.filter((f) => f.w >= size * 0.15));
    // รูปหน้าบนบัตร: เล็กกว่าหน้าจริงชัดเจน และไม่ซ้อนกับหน้าจริง (list เรียงตามคะแนนแล้ว → ได้ตัวที่มั่นใจสุด)
    const findCard = (list, main) => list.find((f) => f !== main && f.w <= main.w * 0.5 && f.w >= main.w * 0.04 && iou(f, main) === 0);

    // รูปบัตรล้วนถ่ายใกล้ หน้าบนบัตรไม่เล็กมาก → ซอย 2 ระดับพอ (เร็วกว่า) / รูปถือบัตร บัตรอาจอยู่ไกล → 3 ระดับ
    const faces = detectAllFaces(detector, canvas, kind === 'holdcard' ? 3 : 2, (list) => {
        if (kind !== 'holdcard') return list.some((f) => f.score >= 0.7);
        const main = findMain(list);
        return !main || !!findCard(list, main);   // ไม่มีหน้าคนจริง = ไม่ผ่านแน่ ไม่ต้องค้นละเอียดต่อ
    });
    const out = { ok: false, code: '', message: '', mirrored: null, rotation: 0, faces, mainFace: null, cardFace: null, chip: null };
    const fail = (code) => { out.code = code; out.message = PHOTO_CHECK_MESSAGES[code]; return out; };

    if (kind === 'holdcard') {
        const main = findMain(faces);
        if (!main) return fail('no-face');
        out.mainFace = main;
        const card = findCard(faces, main);
        if (!card) return fail('no-card');
        out.cardFace = card;
    } else {
        // คะแนนต่ำ = มักเป็นของปลอม (ลายน้ำ/ตัวหนังสือ) ไม่นับเป็นรูปหน้าบนบัตร
        const biggest = largest(faces.filter((f) => f.score >= 0.7));
        if (!biggest) return fail('idcard-not-found');
        if (biggest.w >= size * 0.25) return fail('idcard-is-selfie');
        out.cardFace = biggest;
    }
    out.chip = mirrorFromChip(canvas, out.cardFace);
    out.mirrored = out.chip.mirrored;
    out.ok = true;
    return out;
}

function shrink(canvas) {
    const scale = Math.min(1, MAX_SIDE / Math.max(canvas.width, canvas.height));
    if (scale >= 1) return canvas;
    const c = document.createElement('canvas');
    c.width = Math.round(canvas.width * scale);
    c.height = Math.round(canvas.height * scale);
    c.getContext('2d').drawImage(canvas, 0, 0, c.width, c.height);
    return c;
}

/**
 * วิเคราะห์รูป — คืน { ok, code, message, mirrored, rotation, faces, mainFace, cardFace }
 *   rotation = ต้องหมุนภาพตามเข็มกี่องศาให้บัตรตั้งตรง (เฉพาะ kind 'idcard' — คนมักถ่ายบัตรแนวตะแคง)
 *   mirrored = true/false/null(ตัดสินไม่ได้) — ประเมิน "หลังหมุนตั้งตรงแล้ว"
 * โยน error เมื่อโหลดตัวตรวจไม่ได้ (ผู้เรียกตัดสินใจเองว่าจะปล่อยผ่านหรือไม่)
 */
export async function analyzePhoto(sourceCanvas, kind) {
    const detector = await loadDetector();
    const canvas = shrink(sourceCanvas);
    const first = analyzeUpright(detector, canvas, kind);
    if (kind === 'holdcard') {
        // บัตรที่ถือเอียงเกิน 45° → ตำแหน่งชิปซ้าย/ขวาเชื่อไม่ได้ ให้ผู้ใช้ดูเอง
        if (first.ok && first.cardFace.turn !== 0) first.mirrored = null;
        return first;
    }

    // รูปบัตรล้วนถ่ายด้วยกล้องหลัง แทบไม่มีทางกลับด้าน (ทดสอบกับรูปจริง 194 รูป: ไม่มีรูปกลับด้านจริงเลย)
    // → ไม่กลับด้านอัตโนมัติ (ผู้ใช้กดเองได้ในหน้ายืนยันรูป) แต่ใช้ตำแหน่งชิปเป็นตัว "ยืนยันทิศ" แทน:
    //   บัตรตั้งตรง ชิปต้องอยู่ซ้ายของรูปหน้า — ถ้าชิปไปอยู่ขวา แปลว่าตัวตรวจหน้าอ่านทิศผิด ภาพจริงกลับหัวอยู่
    const upright = (deg) => {
        const r = deg ? analyzeUpright(detector, rotateCanvas(canvas, deg), kind) : first;
        if (!r.ok || r.cardFace.turn !== 0) return null;
        r.rotation = deg;
        r.chipOnRight = r.chip.mirrored === true;
        r.mirrored = null;
        return r;
    };
    const settle = (deg) => {
        const r = upright(deg);
        if (!r || !r.chipOnRight) return r;
        const opposite = upright((deg + 180) % 360);
        return opposite && !opposite.chipOnRight ? opposite : r;
    };
    first.mirrored = null;
    if (first.ok) {
        // เจอหน้าบนบัตรแต่ตะแคง → หมุนให้ตั้งตรงแล้วยืนยันซ้ำ
        return settle(first.cardFace.turn) || first;
    }
    if (first.code === 'idcard-not-found') {
        // ตัวตรวจหน้าไม่ทนต่อภาพตะแคง/กลับหัว → ลองหมุนทีละมุม
        const turns = [90, 270, 180];
        for (let i = 0; i < turns.length; i++) {
            const turned = settle(turns[i]);
            if (turned) return turned;
        }
    }
    return first;
}
